import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * FAQ Schema Component for SEO
 * 
 * Generates JSON-LD structured data for FAQPage schema.org markup.
 * Supports two usage patterns:
 * 
 * 1. SIMPLE (single FAQ section):
 *    <FAQSchema faqs={[{ question: "...", answer: "..." }]} />
 * 
 * 2. MERGED (multiple FAQ sections on one page):
 *    <FAQSchemaProvider>
 *      <FAQSchemaSection faqs={section1Faqs} />
 *      <FAQSchemaSection faqs={section2Faqs} />
 *    </FAQSchemaProvider>
 */

// Context for collecting FAQs from multiple sections
const FAQSchemaContext = createContext(null);

/**
 * Provider component for pages with multiple FAQ sections.
 * Collects all FAQs and renders a single merged JSON-LD block.
 */
export const FAQSchemaProvider = ({ children }) => {
  const [collectedFaqs, setCollectedFaqs] = useState([]);

  const registerFaqs = useCallback((faqs) => {
    setCollectedFaqs(prev => {
      // Avoid duplicates by checking question text
      const existingQuestions = new Set(prev.map(f => f.question));
      const newFaqs = faqs.filter(f => !existingQuestions.has(f.question));
      if (newFaqs.length === 0) return prev; // No change needed
      return [...prev, ...newFaqs];
    });
  }, []);

  const unregisterFaqs = useCallback((faqs) => {
    const questionsToRemove = new Set(faqs.map(f => f.question));
    setCollectedFaqs(prev => {
      const filtered = prev.filter(f => !questionsToRemove.has(f.question));
      if (filtered.length === prev.length) return prev; // No change needed
      return filtered;
    });
  }, []);

  // Memoize context value to prevent re-renders
  const contextValue = useMemo(() => ({
    registerFaqs,
    unregisterFaqs
  }), [registerFaqs, unregisterFaqs]);

  return (
    <FAQSchemaContext.Provider value={contextValue}>
      {children}
      {collectedFaqs.length > 0 && (
        <FAQSchemaRenderer faqs={collectedFaqs} />
      )}
    </FAQSchemaContext.Provider>
  );
};

/**
 * Section component for registering FAQs with the provider.
 * Use inside FAQSchemaProvider for multi-section pages.
 */
export const FAQSchemaSection = ({ faqs }) => {
  const context = useContext(FAQSchemaContext);
  // Use ref to track if we've already registered these FAQs
  const registeredRef = useRef(false);
  // Store stable reference to faqs array
  const faqsRef = useRef(faqs);

  useEffect(() => {
    // Only register once and only if faqs actually changed
    const faqsChanged = JSON.stringify(faqsRef.current) !== JSON.stringify(faqs);
    
    if (context && faqs?.length > 0 && (!registeredRef.current || faqsChanged)) {
      // Unregister old if faqs changed
      if (registeredRef.current && faqsChanged) {
        context.unregisterFaqs(faqsRef.current);
      }
      
      faqsRef.current = faqs;
      context.registerFaqs(faqs);
      registeredRef.current = true;
    }

    return () => {
      if (context && registeredRef.current) {
        context.unregisterFaqs(faqsRef.current);
        registeredRef.current = false;
      }
    };
  }, [context]); // Only depend on context, not faqs (we handle faqs with refs)

  // This component doesn't render anything visible
  return null;
};

/**
 * Internal renderer that generates the JSON-LD script tag.
 */
const FAQSchemaRenderer = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
    </Helmet>
  );
};

/**
 * Simple FAQ Schema component for single-section pages.
 * Use directly when you have only one FAQ section on a page.
 * 
 * @param {Array} faqs - Array of { question: string, answer: string }
 * 
 * @example
 * <FAQSchema faqs={[
 *   { question: "What is an LLC?", answer: "A Limited Liability Company..." },
 *   { question: "How long does it take?", answer: "3-5 business days..." }
 * ]} />
 */
export const FAQSchema = ({ faqs }) => {
  return <FAQSchemaRenderer faqs={faqs} />;
};

export default FAQSchema;
